/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import IEnergyManagementProps, {
  IAny
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import useMounted from '../../shared/useMount/useMount';
import useConnectorAndDynamicProps from '../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import { isObjectOrArrayAndEmpty } from '../../shared/Utils/Utils';
import { Card, Icon, Select, Tab, Tooltip } from '@scuf/common';
import withLoadingStatus from '../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../shared/StatusWrappers/ErrorStatus';
import ConnectionError from '../../shared/ErrorComponents/ConnectionError';
import DataUploadFileTable, { IUploadFile } from './DataUploadFileTable';
import './DataUploadCommon.scss';
import { useFileUploadContext } from '../../../context/FileUploadContext/FileUploadContext';
import FileUploadAction from '../../../context/FileUploadContext/FileUploadActions';
import UploadModal from '../UploadModal/UploadModal';
import { useInterval } from '../../shared/useInterval/useInterval';
import {
  apiNoContentStatusCode,
  MAGIC_VALUE,
  UPLOAD_REFRESH_INTERVAL
} from '../../shared/Utils/Constants';
import AddFuelModal from '../AddFuelModal/AddFuelModal';
import Scope1Table, { ScopeOneData } from '../Scope1Table/Scope1Table';
import {
  GuidEmpty,
  getConnectorConfig,
  getScope2FirstVal,
  getScope2Values,
  getYearValues,
  getupdatedSiteIDWithDetails
} from '../Scope1Table/Scope1TableUtils';
import { getTabsDataUpload } from '../../shared/Utils/CommonWidgetUtils';
import errorIcon from '../../../images/errorIcon.svg';
import Scope2Table, { ScopeTwoData } from '../Scope1Table/Scope2Table';
import { fuelGasTypeLabelCheck } from '../AddFuelModal/AddFuelUtils';

interface IDataUploadContent {
  data: IUploadFile[] | ScopeOneData[] | ScopeTwoData[];
  onRefresh: () => Promise<void>;
  statusCode: number | null;
  activeIndex: number;
  selectedYear: string;
  setSelectedYear: Function;
  toggleFeatureAllUpload?: boolean;
  toggleFeatureScopeOne?: boolean;
  countryCode?: string;
  toggleFeatureGAS?: boolean;
  toggleFeatureScopeTwo?: boolean;
  setSelectedUnit?: any;
  selectedUnit?: string;
}

const DataUploadContent = (props: IDataUploadContent) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const {
    data,
    onRefresh,
    statusCode,
    activeIndex,
    selectedYear,
    setSelectedYear,
    toggleFeatureAllUpload,
    toggleFeatureScopeOne,
    countryCode,
    toggleFeatureGAS,
    toggleFeatureScopeTwo,
    setSelectedUnit,
    selectedUnit
  } = props;
  const getCombustionName = fuelGasTypeLabelCheck(
    activeIndex,
    'StationaryCombustion',
    'Refrigerants',
    toggleFeatureAllUpload,
    toggleFeatureScopeOne,
    toggleFeatureScopeTwo,
    'Scope2',
    toggleFeatureGAS,
    'GAS',
    'dataUpload'
  );

  let updatedData, heddingDisplay, tableComponent;
  if (getCombustionName === 'GAS') {
    let filteredGas = data
      ?.map((val: any) => {
        if (val.fuelType.fuelTypeName === 'Gas') {
          return val;
        }
      })
      .filter((val) => val !== undefined);
    let checkForUpdatedSiteIDWithDetails = getupdatedSiteIDWithDetails(
      filteredGas,
      setSelectedUnit
    );
    if (checkForUpdatedSiteIDWithDetails) {
      updatedData = filteredGas?.map((listOfUtilityType) => {
        let emissionFactorDetailsObject: any;
        let emissionFactorDetailsObjectCount = listOfUtilityType?.emissionFactor?.filter(
          (val: { siteId: string; effectiveFrom: moment.MomentInput }) =>
            val.siteId !== GuidEmpty
        );
        let allTheObjectAreKWHCheck = emissionFactorDetailsObjectCount?.every(
          (val: { emissionFactorUnitByCo2eMeasurement: string }) =>
            val.emissionFactorUnitByCo2eMeasurement === 'kgCo2e/kwh'
        );
        if (
          emissionFactorDetailsObjectCount?.length > 1 &&
          !allTheObjectAreKWHCheck
        ) {
          emissionFactorDetailsObject = listOfUtilityType?.emissionFactor?.filter(
            (val: {
              emissionFactorUnitByCo2eMeasurement: string;
              siteId: string;
              effectiveFrom: moment.MomentInput;
            }) =>
              val.siteId !== GuidEmpty &&
              val.emissionFactorUnitByCo2eMeasurement !== 'kgCo2e/kwh'
          )[0]?.fuelTypeUnitId;
        } else {
          emissionFactorDetailsObject = listOfUtilityType?.emissionFactor?.filter(
            (val: { siteId: string; effectiveFrom: moment.MomentInput }) =>
              val.siteId !== GuidEmpty
          )[0]?.fuelTypeUnitId;
        }

        let filteredEmissionFactor = listOfUtilityType?.emissionFactor?.filter(
          (filteredVal: any) =>
            filteredVal?.fuelTypeUnitId === emissionFactorDetailsObject
        );
        return {
          fuelType: listOfUtilityType?.fuelType,
          fuelTypeUnits: listOfUtilityType?.fuelTypeUnits,
          emissionFactor: filteredEmissionFactor,
          id: listOfUtilityType.id
        };
      });
    } else {
      let unitID = getScope2FirstVal(filteredGas, selectedUnit);
      updatedData =
        unitID &&
        filteredGas?.map((val) => {
          let filteredEmissionFactor = val?.emissionFactor?.filter(
            (filteredVal: any) => filteredVal?.fuelTypeUnitId === unitID
          );
          return {
            fuelType: val.fuelType,
            fuelTypeUnits: val.fuelTypeUnits,
            emissionFactor: filteredEmissionFactor,
            id: val.id
          };
        });
    }

    heddingDisplay = t(d.METEREDGASHEADER);
    tableComponent = (
      <>
        <div className="fuel-table-header">
          <div className="pt-5">{heddingDisplay}</div>
          {filteredGas.length > 0 && (
            <div className="unitDropDownDiv">
              <span>{t(d.UNITDROPDOWNNAME)}</span>
              <Select
                className="unitDropDown"
                value={selectedUnit}
                options={getScope2Values(filteredGas)}
                onChange={(value) => setSelectedUnit(value)}
                disabled={checkForUpdatedSiteIDWithDetails}
              />
            </div>
          )}
        </div>
        <Scope2Table
          data={(updatedData as unknown) as ScopeTwoData[]}
          countryCode={countryCode}
        ></Scope2Table>
      </>
    );
  } else if (getCombustionName === 'Scope2') {
    let filteredScope2 = data
      .map((val: any) => {
        if (val.fuelType.fuelTypeName !== 'Gas') {
          return val;
        }
      })
      .filter((val) => val !== undefined);
    updatedData = filteredScope2;
    heddingDisplay = t(d.SCOPETWOHEADER);
    tableComponent = (
      <>
        <div className="fuel-table-header">
          <div className="pt-5">{heddingDisplay}</div>
        </div>
        <Scope2Table
          data={updatedData as ScopeTwoData[]}
          countryCode={countryCode}
        ></Scope2Table>
      </>
    );
  } else if (getCombustionName === 'Refrigerants') {
    tableComponent = (
      <>
        <div className="fuel-table-header">
          <div className="pt-5">{t(d.COMPOUNDTABLETEXT)}</div>
          <div>
            <Select
              defaultValue={selectedYear}
              options={getYearValues()}
              onChange={(value) => setSelectedYear(value)}
            />
          </div>
        </div>
        <Scope1Table
          data={data as ScopeOneData[]}
          selectedYear={selectedYear}
        />
      </>
    );
  } else if (getCombustionName === 'StationaryCombustion') {
    tableComponent = (
      <>
        <div className="fuel-table-header">
          <div className="pt-5">{t(d.FUELTABLETEXT)}</div>
          <div>
            <Select
              defaultValue={selectedYear}
              options={getYearValues()}
              onChange={(value) => setSelectedYear(value)}
            />
          </div>
        </div>
        <Scope1Table
          data={data as ScopeOneData[]}
          selectedYear={selectedYear}
        />
      </>
    );
  } else if (getCombustionName === 'dataUpload') {
    tableComponent = <DataUploadFileTable data={data as IUploadFile[]} />;
  }

  if (
    isObjectOrArrayAndEmpty(data) &&
    statusCode &&
    !(statusCode == apiNoContentStatusCode)
  ) {
    return (
      <ConnectionError
        title={t(d.CONNECTIONERRORTITLE)}
        message={t(d.CONNECTIONERROR)}
        onRefresh={onRefresh}
      />
    );
  }

  return <div>{tableComponent}</div>;
};

const DataUploadComponentWithLoader = withLoadingStatus(
  withErrorStatus(DataUploadContent)
);

const DataUploadComponent = (props: IEnergyManagementProps) => {
  const [pollingCount, setPollingCount] = useState(0);
  const [selectedYear, setSelectedYear] = useState(
    new Date().getFullYear().toString()
  );
  const [selectedUnit, setSelectedUnit] = useState('kwh');
  const {
    bearerToken,
    requestContext,
    toggleFeatureScopeOne,
    toggleFeatureAllUpload,
    toggleFeatureScopeTwo,
    emaasContext,
    toggleFeatureGAS
  } = props;
  const [activeIndex, SetactiveIndex] = useState(0);
  const [getCancelPopup, setCancelPopup] = useState(false);
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const [state, dispatch] = useFileUploadContext();
  const isMounted = useMounted();
  const connectorConfig = getConnectorConfig(
    activeIndex,
    selectedYear,
    toggleFeatureAllUpload,
    toggleFeatureScopeOne,
    toggleFeatureGAS,
    toggleFeatureScopeTwo,
    emaasContext
  ) as IAny;
  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig: connectorConfig[0],
    requestContext,
    bearerToken
  });
  const [dataConnectorMissingData] = useConnectorAndDynamicProps({
    connectorConfig: connectorConfig[1],
    requestContext,
    bearerToken
  });

  const configVal = { requestContext: requestContext, boolVal: true };
  useEffect(() => {
    if (
      isMounted &&
      state.showModal !== true &&
      state.showAddFuelModal !== true
    ) {
      if (!getCancelPopup) {
        dataConnector &&
          dataConnector.actions &&
          dataConnector.actions.getAll();
        dataConnectorMissingData &&
          dataConnectorMissingData.actions &&
          dataConnectorMissingData.actions.getAll();
      }
    }
  }, [requestContext, state, pollingCount, activeIndex]);

  useEffect(() => {
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
    dataConnectorMissingData &&
      dataConnectorMissingData.actions &&
      dataConnectorMissingData.actions.getAll();
  }, [selectedYear]);

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const removeSilentLoadingonRefresh = () => {
    setIsSilentLoading(false);
    return onRefresh();
  };
  const { all, loading, error, status } = dataConnector.data;
  const {
    all: allMissingData,
    loading: loadingMissingData,
    error: errorMissingData,
    status: statusMissingData
  } = dataConnectorMissingData.data;
  // Refresh every 30 seconds
  useInterval(() => {
    if (toggleFeatureAllUpload && activeIndex === 0) {
      setIsSilentLoading(true);
      setPollingCount(pollingCount + 1);
    }
  }, UPLOAD_REFRESH_INTERVAL);

  const fileRecords = all as IUploadFile[];
  const MissingDataCondition = allMissingData as IAny;
  const isDataAvailable = fileRecords ? true : false;
  const isMissingDataAvailable = MissingDataCondition ? true : false;
  const [silentLoading, setIsSilentLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [noDataCount, setnoDataCount] = useState<number>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState<IUploadFile[]>([]);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData([...fileRecords]);
      } else {
        setData([]);
      }
      setErrorInfo(error);
    }
    setStatusCode(status);
  }, [isDataAvailable, loading, status]);

  const showModal = () => dispatch(FileUploadAction.showUploadModal());
  const showAddFuelModal = () => {
    setCancelPopup(false);
    dispatch(FileUploadAction.showAddFuelModal(configVal));
  };
  const getCommonHOCForDataUpload = (selectedIndex: number) => {
    return (
      <>
        <Card.Content className="data-upload-content-container">
          <DataUploadComponentWithLoader
            loading={isLoading}
            loadingText={t(d.LOADING)}
            data={data}
            hideNoData={true}
            onRefresh={removeSilentLoadingonRefresh}
            title={t(d.CONNECTIONERRORTITLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            statusCode={statusCode}
            silentLoading={silentLoading}
            error={errorInfo}
            noDataMessage={''}
            activeIndex={activeIndex}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            toggleFeatureAllUpload={toggleFeatureAllUpload}
            toggleFeatureScopeOne={toggleFeatureScopeOne}
            countryCode={emaasContext?.countryCode}
            toggleFeatureGAS={toggleFeatureGAS}
            toggleFeatureScopeTwo={toggleFeatureScopeTwo}
            setSelectedUnit={setSelectedUnit}
            selectedUnit={selectedUnit}
          />
        </Card.Content>
      </>
    );
  };
  const getCombustionName = fuelGasTypeLabelCheck(
    activeIndex,
    'StationaryCombustion',
    'Refrigerants',
    toggleFeatureAllUpload,
    toggleFeatureScopeOne,
    toggleFeatureScopeTwo,
    'Scope2',
    toggleFeatureGAS,
    'GAS'
  );
  let subHeader;
  const checkScopeArray = [
    'StationaryCombustion',
    'Refrigerants',
    'GAS',
    'Scope2'
  ];
  useEffect(() => {
    if (
      (isMissingDataAvailable || errorMissingData || statusMissingData) &&
      !loadingMissingData
    ) {
      if (getCombustionName === 'StationaryCombustion') {
        setnoDataCount(
          MissingDataCondition?.combustionTypeWiseAlerts
            ?.stationaryCombustionAlerts
        );
      } else if (getCombustionName === 'Refrigerants') {
        setnoDataCount(
          MissingDataCondition?.combustionTypeWiseAlerts?.refrigerantsAlerts
        );
      } else if (!checkScopeArray.includes(getCombustionName as any)) {
        setnoDataCount(MissingDataCondition?.totalAlerts);
      }
    }
  }, [
    activeIndex,
    isMissingDataAvailable,
    loadingMissingData,
    statusMissingData,
    getCombustionName
  ]);

  if (checkScopeArray.includes(getCombustionName as any)) {
    subHeader = t(d.SCOPEONETWOSUBTITLE);
  } else {
    subHeader = t(d.DATAUPLOADCARBONESUBTITLE);
  }
  const addDetails = (dataLength: IAny, toggleFeatureAllUpload?: boolean) => {
    const getCombustionName = fuelGasTypeLabelCheck(
      activeIndex,
      'StationaryCombustion',
      'Refrigerants',
      toggleFeatureAllUpload,
      toggleFeatureScopeOne,
      toggleFeatureScopeTwo,
      'Scope2',
      toggleFeatureGAS,
      'GAS'
    );

    const checkToggleScopeTwo =
      (toggleFeatureAllUpload && activeIndex === 3) ||
      (!toggleFeatureAllUpload && activeIndex === 2) ||
      (toggleFeatureAllUpload && !toggleFeatureScopeOne && activeIndex === 1) ||
      (!toggleFeatureAllUpload && !toggleFeatureScopeOne && activeIndex === 0);
    if (dataLength < MAGIC_VALUE.FOUR || getCombustionName === 'Scope2') {
      let popupText;
      if (getCombustionName === 'StationaryCombustion') {
        popupText = t(d.ADDFUEL);
      } else if (getCombustionName === 'Refrigerants') {
        popupText = t(d.ADDCOMPOUND);
      } else if (getCombustionName === 'GAS') {
        popupText = t(d.ADDMETEREDGAS);
      } else {
        popupText = t(d.ADDSCOPETWO);
      }

      return (
        <span className="data-upload-right-content" onClick={showAddFuelModal}>
          <Icon root="common" name="badge-plus" size="medium" />
          <span style={{ padding: '0 10px' }}>{popupText}</span>
        </span>
      );
    } else {
      return (
        <Tooltip
          content={t(d.EXCEEDEDLIMIT)}
          element={
            <span className="data-upload-right-content-disable">
              <Icon root="common" name="badge-plus" size="medium" />
              <span style={{ padding: '0 10px' }}>
                {(!toggleFeatureAllUpload &&
                  toggleFeatureScopeOne &&
                  activeIndex === 0) ||
                (toggleFeatureAllUpload &&
                  toggleFeatureScopeOne &&
                  activeIndex === 1)
                  ? t(d.ADDFUEL)
                  : !checkToggleScopeTwo
                  ? t(d.ADDCOMPOUND)
                  : t(d.ADD)}
              </span>
            </span>
          }
          position="left center"
          event="hover"
          hoverable={true}
        />
      );
    }
  };

  return (
    <div className="emaas-data-upload-settings">
      <div className="data-upload-section">
        <div>
          <div className="data-upload-header">
            <div className="data-upload-title">{t(d.CARBONEMISSION)}</div>
          </div>
          <div className="data-upload-content">
            <span>{subHeader}</span>
          </div>
          {toggleFeatureScopeOne &&
          isMissingDataAvailable &&
          noDataCount > 0 ? (
            <div className="data-upload-content-count">
              <img src={errorIcon} />
              <span>
                {noDataCount} {t(d.DATAMISSINGALERTS)}
              </span>
            </div>
          ) : (
            <div className="data-upload-content-count" />
          )}
          <div className="emass-data-upload">
            <Card className="data-upload-boarder">
              <Card.Header className="data-upload-container">
                <div className="data-upload-tab">
                  {toggleFeatureAllUpload && activeIndex === 0 ? (
                    <span
                      style={{ float: 'right' }}
                      className="data-upload-right-content"
                      onClick={showModal}
                    >
                      <Icon root="common" name="badge-plus" size="medium" />
                      <span style={{ padding: '0 10px' }}>{t(d.UPLOAD)}</span>
                    </span>
                  ) : (
                    addDetails(data?.length, toggleFeatureAllUpload)
                  )}
                  <Tab
                    defaultActiveIndex={activeIndex}
                    onTabChange={(index) => {
                      if (activeIndex !== index) {
                        setData([]);
                        SetactiveIndex(index);
                        setSelectedYear(new Date().getFullYear().toString());
                        setCancelPopup(false);
                        setIsLoading(true);
                      }
                    }}
                  >
                    {getTabsDataUpload(
                      t,
                      toggleFeatureScopeOne,
                      getCommonHOCForDataUpload,
                      activeIndex,
                      toggleFeatureAllUpload,
                      toggleFeatureScopeTwo,
                      toggleFeatureGAS
                    ).map((tab) => tab)}
                  </Tab>
                </div>
              </Card.Header>
            </Card>
          </div>
        </div>
        <UploadModal />
        <AddFuelModal
          requestContext={requestContext}
          activeIndexDataUpload={activeIndex}
          toggleFeatureAllUpload={toggleFeatureAllUpload}
          toggleFeatureScopeOne={toggleFeatureScopeOne}
          toggleFeatureScopeTwo={toggleFeatureScopeTwo}
          tableData={data}
          countryCode={emaasContext?.countryCode}
          toggleFeatureGAS={toggleFeatureGAS}
          setCancelPopup={setCancelPopup}
        />
      </div>
    </div>
  );
};
export default DataUploadComponent;
